import { useCallback, useMemo } from 'react';

import { useIsFeatureFlagLoaded } from '@shared/v2/contexts/featureFlags';
import useQueuedCallback from '@shared/v2/hooks/useQueuedCallback/useQueuedCallback';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import { PointsValue } from '@shared/v2/utils/dataLayer';

import { useCustomerSelection } from '@/components/CustomerSelectionProvider';
import { useRoomOptionsData } from '@/v2/components/pages/PackageView/contexts/RoomOptionsContext';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import dataLayer from '@/v2/utils/dataLayer/dataLayer';
import getEcommerceDetailedItem from '@/v2/utils/dataLayer/eventDataHelpers/getEcommerceDetailedItemEventData';
import { Property } from '@/v2/utils/dataLayer/eventDataHelpers/getEcommerceItemEventData';

interface UseViewItemListEventProps {
  originCode?: string;
  property: Property;
  tiers?: any;
}

const useViewItemEvent = ({
  originCode: code,
  property,
  tiers,
}: UseViewItemListEventProps) => {
  const shouldUseNewEvents = useGA4Events();
  const areFeatureFlagsReady = useIsFeatureFlagLoaded();
  const { searchQuery, isReady: isSearchQueryReady } = useSearchQuery();
  const { paymentType } = useCustomerSelection();
  const {
    packageOptions,
    roomOptions,
    isPreSearch,
    selectedRoomOption,
    isLoading,
  } = useRoomOptionsData() || {};

  const isReady = useMemo(
    () =>
      shouldUseNewEvents &&
      areFeatureFlagsReady &&
      isSearchQueryReady &&
      !isLoading,
    [areFeatureFlagsReady, isSearchQueryReady, shouldUseNewEvents, isLoading],
  );

  const handleInvokeViewItemEvent = useCallback(() => {
    if (!isReady) return;

    const originCode = code || searchQuery?.originCode;

    const item = getEcommerceDetailedItem({
      property,
      tiers,
      originCode,
      paymentType: paymentType as PointsValue,
      travellers: {
        adults: searchQuery?.adults ?? 2,
        children: searchQuery?.children ?? 0,
        infants: searchQuery?.infants ?? 0,
      },
      travelDateRange: {
        start: searchQuery?.departureDate,
        end: searchQuery?.returnDate,
      },
      availableOfferCount: (packageOptions ?? []).length + 1,
      availableRoomCount: (roomOptions?.main || []).filter(
        ({ matchingLeadInOffer, matchingOffer }) =>
          isPreSearch ? !!matchingLeadInOffer : !!matchingOffer,
      )?.length,
      leadInRoom: selectedRoomOption ?? (roomOptions?.main || [])[0],
      isPreSearch: !!isPreSearch,
    });

    return dataLayer.viewItemEvent({ item });
  }, [
    isReady,
    code,
    searchQuery?.originCode,
    searchQuery?.adults,
    searchQuery?.children,
    searchQuery?.infants,
    searchQuery?.departureDate,
    searchQuery?.returnDate,
    property,
    tiers,
    paymentType,
    packageOptions,
    roomOptions?.main,
    selectedRoomOption,
    isPreSearch,
  ]);

  const [queuedHandleViewItemDataLayerEvent] = useQueuedCallback(
    handleInvokeViewItemEvent,
    {
      // always queue all events unless flag is ready and should use new events.
      isCallbackReady:
        areFeatureFlagsReady && shouldUseNewEvents && isSearchQueryReady,
      autoRunQueueWhenReady: true,
    },
  );

  return {
    isReady,
    handleInvokeViewItemEvent: queuedHandleViewItemDataLayerEvent as () => void,
  };
};

export default useViewItemEvent;
